"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StreamParser = void 0;
const cfb_interop_1 = require("../../../../../cfb-interop");
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const BinaryReader_1 = require("../BinaryReader");
const PropertyParser_1 = require("../PropertyParser");
const cfbUtil_1 = require("../../../cfbUtil");
const STORAGE_ENTRY_MAGIC = 0xd0;
class StreamParser {
    constructor(cfb) {
        this.cfb = cfb;
    }
    static async fromBlob(blob) {
        const cfb = await (0, cfbUtil_1.loadCfb)(blob);
        return new StreamParser(cfb);
    }
    async parseStorage() {
        const stream = cfb_interop_1.CFB.find(this.cfb, 'Storage');
        if (stream === null) {
            return undefined;
        }
        const content = Array.isArray(stream.content) ? new Uint8Array(stream.content) : stream.content;
        const blob = new Blob([content]);
        const reader = await BinaryReader_1.BinaryReader.fromBlob(blob);
        if (reader.getByteLength() === 0) {
            return undefined;
        }
        const propertyLength = reader.readUint32LE();
        const propertyData = reader.readBytes(propertyLength);
        const properties = PropertyParser_1.PropertyParser.parseProperties(propertyData);
        const weight = parseInt(properties.weight?.value ?? '0', 10);
        if (!reader.hasData()) {
            return { properties, numFiles: 0, files: [], processingResults: [] };
        }
        const files = [];
        const processingResults = [];
        for (let i = 0; i < weight; i++) {
            const { length: declaredLength } = PropertyParser_1.PropertyParser.parsePropertyLength(reader.readUint32LE());
            const bodyStart = reader.getPosition();
            const body = new BinaryReader_1.BinaryReader(reader.readBytes(declaredLength));
            let entry;
            try {
                entry = StreamParser.parseStorageEntry(body);
            }
            catch (error) {
                const reason = error instanceof Error ? error.message : String(error);
                processingResults.push({
                    type: 'ParseStorage_EntrySkipped',
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                    message: `[ParseStorage_EntrySkipped] Entry ${i} at offset ${bodyStart} (${declaredLength} bytes) could not be read: ${reason}`,
                    entryIndex: i,
                    declaredLength,
                    reason,
                });
                continue;
            }
            files.push(entry.file);
            if (entry.consumedLength !== declaredLength) {
                processingResults.push({
                    type: 'ParseStorage_EntryLengthMismatch',
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                    message: `[ParseStorage_EntryLengthMismatch] Entry '${entry.file.fileName}' declares ${declaredLength} bytes but its fields span ${entry.consumedLength}`,
                    fileName: entry.file.fileName,
                    declaredLength,
                    consumedLength: entry.consumedLength,
                    dataLength: entry.dataLength,
                });
            }
        }
        return { properties, numFiles: weight, files, processingResults };
    }
    static parseStorageEntry(body) {
        const magic = body.readUint8();
        if (magic !== STORAGE_ENTRY_MAGIC) {
            throw new Error(`[ParseStorage_InvalidMagic] Expected 0xD0, got 0x${magic.toString(16)}`);
        }
        const fileName = body.readPascalStringShort();
        const dataLength = body.readUint32LE();
        const data = body.readBytes(Math.min(dataLength, body.remaining()));
        const consumedLength = body.getPosition() + Math.max(0, dataLength - data.length);
        return { file: { fileName, data }, consumedLength, dataLength };
    }
    async parseFileHeader() {
        const stream = cfb_interop_1.CFB.find(this.cfb, 'FileHeader');
        if (stream === null) {
            throw new Error('[ParseFileHeader_StreamNotFound] FileHeader stream is required but not found');
        }
        const content = Array.isArray(stream.content) ? new Uint8Array(stream.content) : stream.content;
        const reader = new BinaryReader_1.BinaryReader(content);
        const records = [];
        while (reader.hasData()) {
            const lengthRaw = reader.readUint32LE();
            const { length } = PropertyParser_1.PropertyParser.parsePropertyLength(lengthRaw);
            const propertyData = reader.readBytes(length);
            const properties = PropertyParser_1.PropertyParser.parseProperties(propertyData);
            records.push(properties);
        }
        return records;
    }
    async parseAdditional() {
        const stream = cfb_interop_1.CFB.find(this.cfb, 'Additional');
        if (stream === null) {
            return undefined;
        }
        const content = Array.isArray(stream.content) ? new Uint8Array(stream.content) : stream.content;
        const reader = new BinaryReader_1.BinaryReader(content);
        const records = [];
        while (reader.hasData()) {
            const lengthRaw = reader.readUint32LE();
            const { length } = PropertyParser_1.PropertyParser.parsePropertyLength(lengthRaw);
            const propertyData = reader.readBytes(length);
            const properties = PropertyParser_1.PropertyParser.parseProperties(propertyData);
            records.push(properties);
        }
        return records;
    }
    findStream(path) {
        const stream = cfb_interop_1.CFB.find(this.cfb, path);
        if (stream === null) {
            return undefined;
        }
        const content = Array.isArray(stream.content) ? new Uint8Array(stream.content) : stream.content;
        return content;
    }
}
exports.StreamParser = StreamParser;
//# sourceMappingURL=StreamParser.js.map